import { Component, OnDestroy, effect, inject, input, output, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable } from 'rxjs';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { LanguageService } from '@core/services/language/language.service';
import {
  DrawioEditorDialogComponent,
  DrawioEditorResult
} from '@shared/components/drawio-editor-dialog/drawio-editor-dialog.component';
import { Diagram, DiagramContent, DiagramRequest } from '../../models/diagram.model';

export interface DiagramSaveEvent {
  diagram: Diagram | null; // null = diagrama nuevo
  request: DiagramRequest;
}

/**
 * Lista de diagramas draw.io con miniatura. "Añadir diagrama" abre el editor
 * embebido en blanco; clicar una miniatura lo abre con el XML guardado.
 * Las llamadas HTTP las hace el padre (loaders por input + eventos de salida).
 */
@Component({
  selector: 'app-diagram-list',
  standalone: true,
  host: { '[class.flat]': 'flat()' },
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule, TranslatePipe],
  template: `
    <div class="diagram-list">
      @if (canEdit() && showActions()) {
        <div class="diagram-actions">
          <button mat-stroked-button type="button" (click)="createDiagram()">
            <mat-icon>account_tree</mat-icon>
            {{ 'gdp.diagrams.add' | translate }}
          </button>
        </div>
      }

      @if (diagrams().length > 0) {
        <div class="thumb-grid">
          @for (diagram of diagrams(); track diagram.id) {
            <figure class="thumb" (click)="editDiagram(diagram)">
              @if (urlOf(diagram.id); as url) {
                <img class="thumb-img" [src]="url" [alt]="diagram.name">
              } @else {
                <div class="thumb-loading"><mat-icon>account_tree</mat-icon></div>
              }
              <figcaption class="thumb-caption">{{ diagram.name }}</figcaption>
              <span class="thumb-overlay" (click)="$event.stopPropagation()">
                <button mat-icon-button class="thumb-btn"
                        [matTooltip]="(canEdit() ? 'common.edit' : 'gdp.diagrams.view') | translate"
                        (click)="editDiagram(diagram)">
                  <mat-icon>{{ canEdit() ? 'edit' : 'visibility' }}</mat-icon>
                </button>
                @if (canEdit()) {
                  <button mat-icon-button class="thumb-btn thumb-btn-danger"
                          (click)="delete.emit(diagram)">
                    <mat-icon>delete</mat-icon>
                  </button>
                }
              </span>
            </figure>
          }
        </div>
      } @else if (showEmptyMessage()) {
        <p class="diagram-empty">{{ 'gdp.diagrams.empty' | translate }}</p>
      }
    </div>
  `,
  styles: [`
    /* Modo "flat": el padre proyecta esta lista dentro de su propia rejilla de
       miniaturas. Se disuelven los contenedores (display: contents) para que las
       figuras de diagrama sean items de la rejilla del padre y compartan fila. */
    :host(.flat), :host(.flat) .diagram-list, :host(.flat) .thumb-grid { display: contents; }
    .diagram-list { display: flex; flex-direction: column; gap: 10px; width: 100%; }
    .diagram-actions { display: flex; align-items: center; gap: 8px; }
    .thumb-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
      gap: 10px;
    }
    .thumb {
      position: relative;
      margin: 0;
      border: 1px solid var(--gdp-border, #e6e8f0);
      border-radius: 10px;
      overflow: hidden;
      cursor: pointer;
      background: white;
    }
    .thumb-img {
      display: block;
      width: 100%;
      height: 96px;
      object-fit: contain;
      background: white;
    }
    .thumb-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 96px;
      color: #c3c7d4;
    }
    .thumb-caption {
      font-size: 0.68rem;
      color: var(--gdp-muted, #6b7280);
      padding: 4px 8px;
      background: white;
      border-top: 1px solid var(--gdp-border, #eef0f6);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .thumb-overlay {
      position: absolute;
      inset: 0 0 24px 0;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 2px;
      background: rgba(27, 20, 100, 0.55);
      opacity: 0;
      transition: opacity 0.15s ease;
    }
    .thumb:hover .thumb-overlay { opacity: 1; }
    .thumb-btn { color: white; width: 34px; height: 34px; padding: 5px; }
    .thumb-btn mat-icon { font-size: 19px; width: 19px; height: 19px; }
    .thumb-btn-danger { color: #ff8a80; }
    .diagram-empty { font-size: 0.85rem; color: var(--gdp-muted, #999); margin: 4px 0; }
  `]
})
export class DiagramListComponent implements OnDestroy {
  private readonly dialog = inject(MatDialog);
  private readonly language = inject(LanguageService);

  diagrams = input.required<Diagram[]>();
  canEdit = input<boolean>(true);
  /** Oculta el botón "Añadir diagrama" cuando lo aporta el padre (sección unificada). */
  showActions = input<boolean>(true);
  /**
   * Proyectada dentro de la rejilla de miniaturas del padre: disuelve sus
   * contenedores para que las figuras compartan fila con los adjuntos.
   */
  flat = input<boolean>(false);
  /** Permite suprimir el mensaje de vacío cuando el padre agrupa más listas. */
  showEmptyMessage = input<boolean>(true);
  /** Descarga la preview PNG (el padre conoce el endpoint). */
  previewLoader = input.required<(diagram: Diagram) => Observable<Blob>>();
  /** Descarga el XML para editar. */
  contentLoader = input.required<(diagram: Diagram) => Observable<DiagramContent>>();

  save = output<DiagramSaveEvent>();
  delete = output<Diagram>();

  private readonly objectUrls = signal<Record<number, string>>({});
  private readonly loading = new Set<number>();

  constructor() {
    effect(() => {
      const loader = this.previewLoader();
      for (const diagram of this.diagrams()) {
        if (!diagram.hasPreview || this.objectUrls()[diagram.id] || this.loading.has(diagram.id)) {
          continue;
        }
        this.loading.add(diagram.id);
        untracked(() => loader(diagram).subscribe({
          next: blob => {
            this.loading.delete(diagram.id);
            this.objectUrls.update(urls => ({ ...urls, [diagram.id]: URL.createObjectURL(blob) }));
          },
          error: () => this.loading.delete(diagram.id)
        }));
      }
    });
  }

  ngOnDestroy(): void {
    Object.values(this.objectUrls()).forEach(url => URL.revokeObjectURL(url));
  }

  urlOf(diagramId: number): string | null {
    return this.objectUrls()[diagramId] ?? null;
  }

  /** Fuerza recargar la miniatura tras guardar cambios. */
  refreshThumbnail(diagramId: number): void {
    const url = this.objectUrls()[diagramId];
    if (url) {
      URL.revokeObjectURL(url);
      this.objectUrls.update(urls => {
        const next = { ...urls };
        delete next[diagramId];
        return next;
      });
    }
  }

  createDiagram(): void {
    const name = `${this.language.translate('gdp.diagrams.default-name')} ${new Date().toLocaleDateString()}`;
    this.openEditor(name, null).subscribe(result => {
      if (result) {
        this.save.emit({
          diagram: null,
          request: { name: result.name, xml: result.xml, previewPngBase64: result.previewPngBase64 }
        });
      }
    });
  }

  editDiagram(diagram: Diagram): void {
    this.contentLoader()(diagram).subscribe(content => {
      this.openEditor(diagram.name, content.xml).subscribe(result => {
        if (result && this.canEdit()) {
          this.save.emit({
            diagram,
            request: { name: result.name, xml: result.xml, previewPngBase64: result.previewPngBase64 }
          });
        }
      });
    });
  }

  private openEditor(name: string, xml: string | null): Observable<DrawioEditorResult | undefined> {
    return this.dialog.open(DrawioEditorDialogComponent, {
      data: { name, xml },
      maxWidth: '96vw',
      disableClose: true
    }).afterClosed();
  }
}
